import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, X, Upload } from "lucide-react";
import { newLevel } from "../../../RTK/userlevels/AddLevel";
import { fetchuserLevels } from "../../../RTK/userlevels/GetAll";
import { toast } from "react-toastify";

const AddLevel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { newLevelStatus, newLevelError } = useSelector(
    (state) => state.userLevels
  );
  const [preview, setPreview] = useState(null);
  const [formError, setFormError] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    level: "",
    min_monthly_spending: "",
    profit_percentage: "",
    hex_color: "#fec111",
    icon: null,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      level: "",
      min_monthly_spending: "",
      profit_percentage: "",
      hex_color: "#fec111",
      icon: null
    });
    setPreview(null);
    setFormError({});
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setFormData({ ...formData, icon: file });
        setPreview(URL.createObjectURL(file));
        setFormError({ ...formError, image: null });
      } else {
        setFormError({ ...formError, image: "Please select an image file." });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });
      await dispatch(newLevel(data)).unwrap();
      await dispatch(fetchuserLevels()).unwrap();
      toast.success("Level added successfully!");
      resetForm();
      onClose();
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Failed to add Level.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[5000]">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md p-6 z-[5500] shadow-2xl border border-gray-700 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-white mb-6 text-right">
          إضافة مستوى جديد
        </h2>

        {newLevelError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-right">
            {newLevelError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center bg-[#2a2a2a] p-4 rounded">
            <div className="img-input flex items-center gap-2 py-2">
              <img
                src={preview || "avatar.png"}
                alt="Preview"
                className="w-[60px] h-[60px] rounded-full border border-[#FEC111] object-cover"
              />
              <label
                htmlFor="file"
                className="cursor-pointer border-b border-[#FEC111] text-[#FEC111] font-semibold"
              >
                Upload Image
              </label>
            </div>
            <input
              type="file"
              id="file"
              name="icon"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />
            {formError.image && (
              <p className="text-red-500 text-sm mt-2">{formError.image}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">

            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                الحد الأدنى للإنفاق
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.min_monthly_spending}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    min_monthly_spending: e.target.value,
                  })
                }
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                اسم المستوى
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                dir="rtl"
                required
              />
            </div>

          </div>

          <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                  المستوى
                </label>
                <input
                  type="number"
                  value={formData.level}
                  onChange={(e) =>
                    setFormData({ ...formData, level: e.target.value })
                  }
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                  dir="rtl"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 text-right mb-1">
                  نسبة الربح (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.profit_percentage}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      profit_percentage: e.target.value,
                    })
                  }
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                  dir="rtl"
                  required
                />
              </div>
            </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 text-right mb-1">
              اللون
            </label>
            <input
              type="color"
              value={formData.hex_color}
              onChange={(e) =>
                setFormData({ ...formData, hex_color: e.target.value })
              }
              className="w-full h-10 bg-gray-700/50 border border-gray-600 rounded-lg p-1 cursor-pointer"
            />
          </div>

          

          <div className="flex justify-between gap-4 mt-8">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 ${
                loading ? "cursor-not-allowed opacity-70" : ""
              }`}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-500 flex items-center gap-2 ${
                loading ? "cursor-not-allowed opacity-70" : ""
              }`}
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              إضافة المستوى
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AddLevel;
